import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

// Base = the pre-Stage-4 class list, byte for byte, so `<Card>` with no props
// renders exactly what every app already ships. `default` re-states `border`
// and `shadow-xs`; tailwind-merge collapses the duplicates, and the other
// variants override them the same way (`shadow-none`, `shadow-lg`, ...).
//
// Rebel token mapping (docs/plans/260909_design-system-umbrella, Stage 4):
//   --color-border-strong   -> border-foreground/20
//   --shadow-medium         -> shadow-lg
//   --glass-panel-bg        -> bg-card/90
//   --glass-panel-border    -> border-border/85
//   --glass-panel-shadow    -> shadow-xl
//   --glass-panel-blur 24px -> backdrop-blur-xl
const cardVariants = cva(
  "bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-xs",
  {
    variants: {
      variant: {
        default: "border shadow-xs",
        outlined: "border border-foreground/20 bg-transparent shadow-none",
        elevated: "border-transparent shadow-lg",
        glass: "border-border/85 bg-card/90 shadow-xl backdrop-blur-xl",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * Card groups related content on a raised surface. `variant` mirrors Rebel's
 * `default | outlined | elevated | glass` vocabulary: `outlined` drops the fill
 * and strengthens the border, `elevated` swaps the border for depth, and
 * `glass` is the translucent, blurred panel used over imagery or gradients.
 */
function Card({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      data-variant={variant ?? "default"}
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-title" className={cn("leading-none font-semibold", className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-description" className={cn("text-muted-foreground text-sm", className)} {...props} />;
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("px-6", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="card-footer" className={cn("flex items-center px-6 [.border-t]:pt-6", className)} {...props} />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent, cardVariants };
